import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { ProfitSpreadsheet } from '#bv/components/internal/ProfitSpreadsheet'
import '#bv/styles/revenue-dashboard.css'
import { loadConsolidatedRevenueFn } from '#bv/lib/revenueDashboardLoadFn'
import {
  utcLastFullMonthRange,
  utcMonthBeforeLastFullMonthRange,
  utcMonthBounds,
  utcAlchemyOwnerFourMonthsRange,
  utcRollingMonthsRange,
  utcThisMonthToDateRange,
} from '#bv/lib/revenueDateRanges'

export const Route = createFileRoute('/internal/revenue')({
  head: () => ({
    meta: [{ name: 'robots', content: 'noindex,nofollow' }],
    title: 'Internal revenue — PulseDigital',
  }),
  component: InternalRevenuePage,
})

function utcTodayDateInput(): string {
  const now = new Date()
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())).toISOString().slice(0, 10)
}

function fmtUsd(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}

function fmtMonthName(iso: string): string {
  return new Date(iso + 'T00:00:00Z').toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
}

type RevenueTab = 'revenue' | 'profit'

const BRAND_SLUG = { Alchemy: 'alchemy', Zenith: 'zenith', blackvault: 'blackvault' } as const

function InternalRevenuePage() {
  const defaults = useMemo(() => utcMonthBounds(), [])
  const [activeTab, setActiveTab] = useState<RevenueTab>('revenue')
  const [secret, setSecret] = useState('')
  const [start, setStart] = useState(defaults.start.slice(0, 10))
  const [end, setEnd] = useState(defaults.end.slice(0, 10))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [json, setJson] = useState<unknown>(null)
  const [monthCompare, setMonthCompare] = useState<{ thisMonth: unknown; lastMonth: unknown; beforeLastMonth: unknown } | null>(null)
  const [rollingMonths, setRollingMonths] = useState(3)
  const [rollingLabel, setRollingLabel] = useState<string | null>(null)

  const run = useCallback(async () => {
    setLoading(true)
    setError(null)
    setJson(null)
    setMonthCompare(null)
    setRollingLabel(null)
    try {
      const endDay = end < utcTodayDateInput() ? utcTodayDateInput() : end
      if (endDay !== end) setEnd(endDay)
      const startIso = `${start}T00:00:00.000Z`
      const endIso = `${endDay}T23:59:59.999Z`
      const data = await loadConsolidatedRevenueFn({ data: { secret, start: startIso, end: endIso } })
      setJson(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [secret, start, end])

  const runMonthCompare = useCallback(async () => {
    setLoading(true)
    setError(null)
    setMonthCompare(null)
    setRollingLabel(null)
    try {
      const beforeLast = utcMonthBeforeLastFullMonthRange()
      const last = utcLastFullMonthRange()
      const cur = utcThisMonthToDateRange()
      const [beforeLastMonth, lastMonth, thisMonth] = await Promise.all([
        loadConsolidatedRevenueFn({ data: { secret, start: beforeLast.startIso, end: beforeLast.endIso } }),
        loadConsolidatedRevenueFn({ data: { secret, start: last.startIso, end: last.endIso } }),
        loadConsolidatedRevenueFn({ data: { secret, start: cur.startIso, end: cur.endIso } }),
      ])
      setMonthCompare({ beforeLastMonth, lastMonth, thisMonth })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [secret])

  const runRollingMonths = useCallback(async () => {
    setLoading(true)
    setError(null)
    setJson(null)
    setMonthCompare(null)
    try {
      const range = utcRollingMonthsRange(rollingMonths)
      setStart(range.startIso.slice(0, 10))
      setEnd(range.endIso.slice(0, 10))
      const data = await loadConsolidatedRevenueFn({
        data: { secret, start: range.startIso, end: range.endIso },
      })
      setJson(data)
      setRollingLabel(range.label)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [secret, rollingMonths])

  const runAlchemyOwnerWindow = useCallback(async () => {
    setLoading(true)
    setError(null)
    setJson(null)
    setMonthCompare(null)
    try {
      const range = utcAlchemyOwnerFourMonthsRange()
      setRollingMonths(4)
      setStart(range.startIso.slice(0, 10))
      setEnd(range.endIso.slice(0, 10))
      const data = await loadConsolidatedRevenueFn({
        data: { secret, start: range.startIso, end: range.endIso },
      })
      setJson(data)
      setRollingLabel(`${range.label} — matches Buy Alchemy /admin/o 4-month total`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [secret])

  const payload = json as {
    ok?: boolean
    grand_total_usd?: number
    blackvault_shop_usd?: number
    combined_total_usd?: number
    alchemy?: { usd: number; rows: number; skipped_parse: number; error?: string }
    zenith?: { usd: number; rows: number; skipped_parse: number; error?: string }
    blackvault?: {
      usd: number
      rows: number
      moneymotion_usd: number
      manual_ff_usd: number
      crypto_usd: number
      error?: string
    }
    product_breakdown?: {
      zenith: Array<{ product: string; qty: number; usd: number }>
      alchemy: Array<{ product: string; qty: number; usd: number }>
      blackvault: Array<{ product: string; qty: number; usd: number }>
    }
    category_counts?: {
      zenith_lifetime: number
      zenith_monthly: number
      alchemy_lifetime: number
      alchemy_token: number
      alchemy_30_days: number
    }
    category_totals?: {
      zenith_lifetime_usd: number
      zenith_monthly_usd: number
      alchemy_lifetime_usd: number
      alchemy_token_usd: number
      alchemy_30_days_usd: number
      blackvault_discord_usd: number
      blackvault_streaming_usd: number
      blackvault_gaming_usd: number
      blackvault_social_usd: number
      blackvault_software_usd: number
    }
    notes?: string[]
    warnings?: string[]
    audit?: {
      zenith_raw_rows: number
      zenith_excluded_blackvault: number
      zenith_excluded_alchemy: number
      zenith_excluded_non_zenith: number
      zenith_excluded_incomplete: number
      zenith_excluded_out_of_range: number
      zenith_deduped: number
      zenith_counted_rows: number
      alchemy_raw_rows: number
      alchemy_excluded_other_brand: number
      alchemy_excluded_not_fulfilled: number
      alchemy_excluded_out_of_range: number
      alchemy_deduped: number
      alchemy_counted_rows: number
      sum_alchemy_usd: number
      sum_zenith_usd: number
      sum_blackvault_usd: number
      combined_total_usd: number
      totals_balanced: boolean
    }
    range?: { start: string; end: string }
    eur_to_usd_rate?: number
    alchemy_eur_to_usd_rate?: number
    zenith_eur_to_usd_rate?: number
  } | null

  const compareThis = (monthCompare?.thisMonth ?? null) as typeof payload
  const compareLast = (monthCompare?.lastMonth ?? null) as typeof payload
  const compareBeforeLast = (monthCompare?.beforeLastMonth ?? null) as typeof payload
  const compareRanges = useMemo(() => {
    return {
      thisMonth: utcThisMonthToDateRange(),
      lastMonth: utcLastFullMonthRange(),
      beforeLastMonth: utcMonthBeforeLastFullMonthRange(),
    }
  }, [])

  const siteTheme = useMemo(() => {
    return {
      alchemy: {
        label: 'Alchemy',
        accent: '#86efac',
        border: 'rgba(34,197,94,0.22)',
        glow: 'rgba(34,197,94,0.18)',
        bg: 'rgba(34,197,94,0.06)',
        barColor: '#22c55e',
      },
      zenith: {
        label: 'Zenith',
        accent: '#fca5a5',
        border: 'rgba(239,68,68,0.22)',
        glow: 'rgba(239,68,68,0.16)',
        bg: 'rgba(239,68,68,0.055)',
        barColor: '#ef4444',
      },
      blackvault: {
        label: 'PulseDigital',
        accent: '#d4e8ff',
        border: 'rgba(0, 191, 255,0.22)',
        glow: 'rgba(0, 191, 255,0.16)',
        bg: 'rgba(0, 191, 255,0.06)',
        barColor: '#00bfff',
      },
    } as const
  }, [])

  const thisBvTotal = compareThis?.blackvault_shop_usd ?? compareThis?.blackvault?.usd ?? 0
  const lastTotal = compareLast?.combined_total_usd ?? compareLast?.grand_total_usd ?? 0
  const thisTotal = compareThis?.combined_total_usd ?? compareThis?.grand_total_usd ?? 0
  const monthDelta = thisTotal - lastTotal
  const monthDeltaPct = lastTotal > 0 ? (monthDelta / lastTotal) * 100 : null
  const hasResults = payload?.ok || (compareThis?.ok && compareLast?.ok && compareBeforeLast?.ok)

  return (
    <div className="rv-page">
      <div className="rv-bg" aria-hidden />
      <div className="rv-shell">
        <header className="rv-topbar">
          <div className="rv-brand">
            <div className="rv-brand-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00bfff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <div>
              <h1 className="rv-brand-title font-display">Revenue</h1>
              <p className="rv-brand-sub">Alchemy · Zenith · PulseDigital</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="rv-tabs" role="tablist">
              {([
                { id: 'revenue' as const, label: 'Revenue' },
                { id: 'profit' as const, label: 'Profit' },
              ]).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`rv-tab${activeTab === tab.id ? ' rv-tab--active' : ''}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {hasResults ? (
              <div className="rv-status" title="Last loaded snapshot — click Load again for fresh numbers">
                <span className="rv-status-dot" />
                Snapshot loaded
              </div>
            ) : null}
          </div>
        </header>

        {activeTab === 'revenue' ? (
          <section className="rv-command">
            <div className="rv-command-shimmer" aria-hidden />
            <div className="rv-command-inner">
              <div className="rv-field-row rv-field-row--main">
                <div>
                  <label className="rv-field-label" htmlFor="rv-secret">Dashboard secret</label>
                  <input
                    id="rv-secret"
                    type="password"
                    autoComplete="off"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    className="bv-input w-full rounded-xl px-4 py-3 text-sm"
                    placeholder="Enter secret to load data"
                  />
                </div>
                <div>
                  <label className="rv-field-label" htmlFor="rv-start">Start (UTC)</label>
                  <input id="rv-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bv-input w-full rounded-xl px-4 py-3 text-sm" />
                </div>
                <div>
                  <label className="rv-field-label" htmlFor="rv-end">End (UTC)</label>
                  <input id="rv-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bv-input w-full rounded-xl px-4 py-3 text-sm" />
                </div>
              </div>
              <div className="rv-actions">
                <button type="button" disabled={loading || !secret.trim()} onClick={() => void run()} className="rv-btn rv-btn--primary">
                  {loading ? <LoadingDots /> : 'Load totals'}
                </button>
                <button type="button" disabled={loading || !secret.trim()} onClick={() => void runMonthCompare()} className="rv-btn rv-btn--ghost">
                  {loading ? <LoadingDots /> : 'Month comparison'}
                </button>
              </div>
              <div className="rv-presets">
                <div className="rv-presets-input">
                  <label className="rv-field-label" htmlFor="rv-rolling">Rolling window</label>
                  <input
                    id="rv-rolling"
                    type="number"
                    min={1}
                    max={24}
                    value={rollingMonths}
                    onChange={(e) => {
                      const n = Number.parseInt(e.target.value, 10)
                      setRollingMonths(Number.isFinite(n) ? Math.min(24, Math.max(1, n)) : 3)
                    }}
                    className="bv-input w-full rounded-xl px-4 py-3 text-sm tabular-nums"
                  />
                </div>
                <button type="button" disabled={loading || !secret.trim()} onClick={() => void runRollingMonths()} className="rv-btn rv-btn--ghost">
                  {loading ? <LoadingDots /> : `${rollingMonths}-month total`}
                </button>
                <button
                  type="button"
                  disabled={loading || !secret.trim()}
                  onClick={() => void runAlchemyOwnerWindow()}
                  className="rv-btn rv-btn--emerald"
                  title="Same UTC window and pricing as Buy Alchemy owner stats"
                >
                  {loading ? <LoadingDots /> : 'Alchemy 4-mo'}
                </button>
              </div>
              <p className="m-0 text-[0.65rem] leading-relaxed" style={{ color: '#5a6d8a' }}>
                Snapshot on demand — totals do not auto-refresh. Use Month comparison to match each brand&apos;s owner page.
              </p>
              {error ? (
                <div className="rv-alert" role="alert">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                  {error}
                </div>
              ) : null}
            </div>
          </section>
        ) : (
          <ProfitSpreadsheet
            productBreakdown={payload?.product_breakdown?.blackvault ?? null}
            eurToUsdRate={payload?.eur_to_usd_rate ?? null}
          />
        )}

        {activeTab === 'revenue' && hasResults ? (
          <div className="rv-dashboard rv-rise">
            {payload?.ok && rollingLabel ? (
              <RollingMonthsSummary
                label={rollingLabel}
                monthCount={rollingMonths}
                combined={payload.combined_total_usd ?? payload.grand_total_usd ?? 0}
                blackvaultShop={payload.blackvault_shop_usd ?? payload.blackvault?.usd ?? 0}
                alchemy={payload.alchemy?.usd ?? 0}
                zenith={payload.zenith?.usd ?? 0}
                blackvault={payload.blackvault?.usd ?? 0}
                theme={siteTheme}
              />
            ) : null}

            {payload?.ok ? (
              <section className="rv-rise rv-rise-delay-1">
                <div className="rv-hero">
                  <div className="rv-hero-shimmer" aria-hidden />
                  <div className="rv-hero-inner">
                    <div className="rv-hero-label">Combined all brands</div>
                    <div className="rv-hero-amount font-display">{fmtUsd(payload.combined_total_usd ?? payload.grand_total_usd ?? 0)}</div>
                    <div className="rv-hero-meta">
                      <span>PulseDigital shop <strong>{fmtUsd(payload.blackvault_shop_usd ?? payload.blackvault?.usd ?? 0)}</strong></span>
                      {payload.alchemy_eur_to_usd_rate != null ? (
                        <span className="rv-pill">Alchemy FX {payload.alchemy_eur_to_usd_rate.toFixed(4)}</span>
                      ) : payload.eur_to_usd_rate ? (
                        <span className="rv-pill">Alchemy FX {payload.eur_to_usd_rate.toFixed(4)}</span>
                      ) : null}
                      {payload.zenith_eur_to_usd_rate != null ? (
                        <span className="rv-pill">Zenith FX {payload.zenith_eur_to_usd_rate.toFixed(4)}</span>
                      ) : null}
                      <span className="rv-pill">
                        {(payload.alchemy?.rows ?? 0) + (payload.zenith?.rows ?? 0) + (payload.blackvault?.rows ?? 0)} rows
                      </span>
                      {payload.audit?.totals_balanced ? (
                        <span className="rv-pill rv-pill--ok">Totals balanced</span>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="rv-brand-grid" style={{ marginTop: 14 }}>
                  <SiteCard title="Alchemy" brand={BRAND_SLUG.Alchemy} theme={siteTheme.alchemy} usd={payload.alchemy?.usd ?? 0} rows={payload.alchemy?.rows ?? 0} skipped={payload.alchemy?.skipped_parse ?? 0} err={payload.alchemy?.error} grandTotal={payload.combined_total_usd ?? payload.grand_total_usd ?? 1}
                    extra={<p className="rv-hint" style={{ color: '#4a6b55' }}>Matches Buy Alchemy /admin/o</p>}
                  />
                  <SiteCard title="Zenith" brand={BRAND_SLUG.Zenith} theme={siteTheme.zenith} usd={payload.zenith?.usd ?? 0} rows={payload.zenith?.rows ?? 0} skipped={payload.zenith?.skipped_parse ?? 0} err={payload.zenith?.error} grandTotal={payload.combined_total_usd ?? payload.grand_total_usd ?? 1}
                    extra={<p className="rv-hint" style={{ color: '#6b4a4a' }}>MM charged EUR (VAT-inclusive) — matches MM checkout totals</p>}
                  />
                  <SiteCard title="PulseDigital" brand={BRAND_SLUG.blackvault} theme={siteTheme.blackvault} usd={payload.blackvault?.usd ?? 0} rows={payload.blackvault?.rows ?? 0} skipped={0} err={payload.blackvault?.error} grandTotal={payload.combined_total_usd ?? payload.grand_total_usd ?? 1}
                    extra={
                      <div className="rv-bv-channels">
                        <div><div style={{ color: '#5a6d8a' }}>{fmtUsd(payload.blackvault?.moneymotion_usd ?? 0)}</div>MM</div>
                        <div><div style={{ color: '#5a6d8a' }}>{fmtUsd(payload.blackvault?.manual_ff_usd ?? 0)}</div>FF</div>
                        <div><div style={{ color: '#5a6d8a' }}>{fmtUsd(payload.blackvault?.crypto_usd ?? 0)}</div>Crypto</div>
                      </div>
                    }
                  />
                </div>
              </section>
            ) : null}

            {payload?.ok ? (
              <div className="rv-rise rv-rise-delay-2">
                <RevenueShareBar alchemy={payload.alchemy?.usd ?? 0} zenith={payload.zenith?.usd ?? 0} blackvault={payload.blackvault?.usd ?? 0} theme={siteTheme} />
              </div>
            ) : null}

            {compareThis?.ok && compareLast?.ok && compareBeforeLast?.ok ? (
              <section className="rv-rise rv-rise-delay-2">
                <div className="rv-section-head">
                  <div className="rv-section-title">
                    <span className="rv-section-title-dot" />
                    Month momentum
                  </div>
                </div>
                <div className="rv-momentum">
                  {([
                    { label: fmtMonthName(compareRanges.beforeLastMonth.startIso), usd: compareBeforeLast.grand_total_usd ?? 0, color: '#6366f1', prev: null as number | null },
                    { label: fmtMonthName(compareRanges.lastMonth.startIso), usd: compareLast.grand_total_usd ?? 0, color: '#a855f7', prev: compareBeforeLast.grand_total_usd ?? 0 },
                    { label: `${fmtMonthName(compareRanges.thisMonth.startIso)} MTD`, usd: compareThis.grand_total_usd ?? 0, color: '#00bfff', prev: compareLast.grand_total_usd ?? 0 },
                  ] as const).map((m) => {
                    const maxUsd = Math.max(compareBeforeLast.grand_total_usd ?? 0, compareLast.grand_total_usd ?? 0, compareThis.grand_total_usd ?? 0, 1)
                    const barPct = Math.round((m.usd / maxUsd) * 100)
                    const delta = m.prev != null ? m.usd - m.prev : null
                    const deltaPct = delta != null && m.prev! > 0 ? (delta / m.prev!) * 100 : null
                    return (
                      <div key={m.label} className="rv-momentum-card">
                        <div className="rv-momentum-fill" style={{ height: `${barPct}%`, background: `linear-gradient(to top, ${m.color}, transparent)` }} />
                        <div className="relative">
                          <div className="flex items-center justify-between gap-2">
                            <div className="rv-momentum-label">{m.label}</div>
                            {delta != null ? (
                              <span className={`rv-delta ${delta >= 0 ? 'rv-delta--up' : 'rv-delta--down'}`}>
                                {delta >= 0 ? '▲' : '▼'}{deltaPct != null ? ` ${Math.abs(deltaPct).toFixed(0)}%` : ''}
                              </span>
                            ) : null}
                          </div>
                          <div className="rv-momentum-amount font-display">{fmtUsd(m.usd)}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>
            ) : null}

            <section className="rv-bento rv-rise rv-rise-delay-3">
            <div className="rv-section-head">
              <div className="rv-section-title">
                <span className="rv-section-title-dot" />
                Range breakdown
              </div>
              {compareThis?.ok && compareLast?.ok ? (
                <div className={`rv-delta-badge ${monthDelta >= 0 ? 'rv-delta-badge--up' : 'rv-delta-badge--down'}`}>
                  <span>{monthDelta >= 0 ? '▲' : '▼'}</span>
                  <span>{monthDelta >= 0 ? '+' : ''}{fmtUsd(monthDelta)}</span>
                  {monthDeltaPct != null ? <span style={{ opacity: 0.65 }}>({monthDeltaPct >= 0 ? '+' : ''}{monthDeltaPct.toFixed(1)}%)</span> : null}
                  <span style={{ opacity: 0.45 }}>vs last month</span>
                </div>
              ) : null}
            </div>

            <div className="grid gap-5 lg:contents">
              <div className="rv-panel">
                <div className="rv-panel-label">Before last + last</div>
                <div className="rv-panel-range">
                  {compareRanges.beforeLastMonth.startIso.slice(0, 10)} → {compareRanges.lastMonth.endIso.slice(0, 10)}
                </div>
                {compareBeforeLast?.ok && compareLast?.ok ? (
                  <div className="rv-panel-stack">
                    <MonthPairCard brand={BRAND_SLUG.Alchemy} title="Alchemy" theme={siteTheme.alchemy} leftLabel="Before last" leftUsd={compareBeforeLast.alchemy?.usd ?? 0} rightLabel="Last" rightUsd={compareLast.alchemy?.usd ?? 0}
                      breakdown={[
                        { label: `Lifetime (${compareBeforeLast.category_counts?.alchemy_lifetime ?? 0} \u2192 ${compareLast.category_counts?.alchemy_lifetime ?? 0})`, leftUsd: compareBeforeLast.category_totals?.alchemy_lifetime_usd ?? 0, rightUsd: compareLast.category_totals?.alchemy_lifetime_usd ?? 0 },
                        { label: `Token (${compareBeforeLast.category_counts?.alchemy_token ?? 0} \u2192 ${compareLast.category_counts?.alchemy_token ?? 0})`, leftUsd: compareBeforeLast.category_totals?.alchemy_token_usd ?? 0, rightUsd: compareLast.category_totals?.alchemy_token_usd ?? 0 },
                        { label: `30 Days (${compareBeforeLast.category_counts?.alchemy_30_days ?? 0} \u2192 ${compareLast.category_counts?.alchemy_30_days ?? 0})`, leftUsd: compareBeforeLast.category_totals?.alchemy_30_days_usd ?? 0, rightUsd: compareLast.category_totals?.alchemy_30_days_usd ?? 0 },
                      ]}
                      leftProducts={compareBeforeLast.product_breakdown?.alchemy ?? []} rightProducts={compareLast.product_breakdown?.alchemy ?? []}
                    />
                    <MonthPairCard brand={BRAND_SLUG.Zenith} title="Zenith" theme={siteTheme.zenith} leftLabel="Before last" leftUsd={compareBeforeLast.zenith?.usd ?? 0} rightLabel="Last" rightUsd={compareLast.zenith?.usd ?? 0}
                      breakdown={[
                        { label: `Lifetime (${compareBeforeLast.category_counts?.zenith_lifetime ?? 0} \u2192 ${compareLast.category_counts?.zenith_lifetime ?? 0})`, leftUsd: compareBeforeLast.category_totals?.zenith_lifetime_usd ?? 0, rightUsd: compareLast.category_totals?.zenith_lifetime_usd ?? 0 },
                        { label: `Monthly (${compareBeforeLast.category_counts?.zenith_monthly ?? 0} \u2192 ${compareLast.category_counts?.zenith_monthly ?? 0})`, leftUsd: compareBeforeLast.category_totals?.zenith_monthly_usd ?? 0, rightUsd: compareLast.category_totals?.zenith_monthly_usd ?? 0 },
                      ]}
                      leftProducts={compareBeforeLast.product_breakdown?.zenith ?? []} rightProducts={compareLast.product_breakdown?.zenith ?? []}
                    />
                    <MonthPairCard brand={BRAND_SLUG.blackvault} title="PulseDigital" theme={siteTheme.blackvault} leftLabel="Before last" leftUsd={compareBeforeLast.blackvault?.usd ?? 0} rightLabel="Last" rightUsd={compareLast.blackvault?.usd ?? 0}
                      breakdown={[
                        { label: 'Discord', leftUsd: compareBeforeLast.category_totals?.blackvault_discord_usd ?? 0, rightUsd: compareLast.category_totals?.blackvault_discord_usd ?? 0 },
                        { label: 'Streaming', leftUsd: compareBeforeLast.category_totals?.blackvault_streaming_usd ?? 0, rightUsd: compareLast.category_totals?.blackvault_streaming_usd ?? 0 },
                        { label: 'Gaming', leftUsd: compareBeforeLast.category_totals?.blackvault_gaming_usd ?? 0, rightUsd: compareLast.category_totals?.blackvault_gaming_usd ?? 0 },
                        { label: 'Social', leftUsd: compareBeforeLast.category_totals?.blackvault_social_usd ?? 0, rightUsd: compareLast.category_totals?.blackvault_social_usd ?? 0 },
                        { label: 'Software', leftUsd: compareBeforeLast.category_totals?.blackvault_software_usd ?? 0, rightUsd: compareLast.category_totals?.blackvault_software_usd ?? 0 },
                      ]}
                      leftProducts={compareBeforeLast.product_breakdown?.blackvault ?? []} rightProducts={compareLast.product_breakdown?.blackvault ?? []}
                    />
                  </div>
                ) : (
                  <EmptyState text="Load month comparison to populate." />
                )}
              </div>

              {payload?.ok ? (
                <div className="rv-panel rv-panel--center">
                  <div className="rv-panel-label">Categories in range</div>
                  {payload.range ? (
                    <div className="rv-panel-range">
                      {payload.range.start.slice(0, 10)} → {payload.range.end.slice(0, 10)}
                    </div>
                  ) : null}
                  <div className="rv-cat-grid">
                    <CategoryMiniCard title="Zenith" accent={siteTheme.zenith.accent} siteUsd={payload.zenith?.usd ?? 0} items={[
                      { label: 'Lifetime', usd: payload.category_totals?.zenith_lifetime_usd ?? 0 },
                      { label: 'Monthly', usd: payload.category_totals?.zenith_monthly_usd ?? 0 },
                    ]} />
                    <CategoryMiniCard title="Alchemy" accent={siteTheme.alchemy.accent} siteUsd={payload.alchemy?.usd ?? 0} items={[
                      { label: 'Lifetime', usd: payload.category_totals?.alchemy_lifetime_usd ?? 0 },
                      { label: 'Token', usd: payload.category_totals?.alchemy_token_usd ?? 0 },
                      { label: '30 Days', usd: payload.category_totals?.alchemy_30_days_usd ?? 0 },
                    ]} />
                    <CategoryMiniCard title="PulseDigital" accent={siteTheme.blackvault.accent} siteUsd={payload.blackvault?.usd ?? 0} items={[
                      { label: 'Discord', usd: payload.category_totals?.blackvault_discord_usd ?? 0 },
                      { label: 'Streaming', usd: payload.category_totals?.blackvault_streaming_usd ?? 0 },
                      { label: 'Gaming', usd: payload.category_totals?.blackvault_gaming_usd ?? 0 },
                      { label: 'Social', usd: payload.category_totals?.blackvault_social_usd ?? 0 },
                      { label: 'Software', usd: payload.category_totals?.blackvault_software_usd ?? 0 },
                    ]} />
                  </div>

                  {payload.audit ? (
                    <div className="rv-recon">
                      <div className="rv-panel-label">Reconciliation</div>
                      <div className="mt-2 space-y-1">
                        <div>
                          Zenith: {payload.audit.zenith_raw_rows} fetched → {payload.audit.zenith_counted_rows} in range → {payload.zenith?.rows ?? 0} priced
                          {(payload.zenith?.skipped_parse ?? 0) > 0 ? ` (${payload.zenith?.skipped_parse} unpriced)` : ''}
                        </div>
                        <div>
                          Alchemy: {payload.audit.alchemy_raw_rows} fetched → {payload.audit.alchemy_counted_rows} in range → {payload.alchemy?.rows ?? 0} priced
                          {(payload.alchemy?.skipped_parse ?? 0) > 0 ? ` (${payload.alchemy?.skipped_parse} unpriced)` : ''}
                        </div>
                        <div>
                          blackvault: {payload.blackvault?.rows ?? 0} orders (MM {fmtUsd(payload.blackvault?.moneymotion_usd ?? 0)} · FF {fmtUsd(payload.blackvault?.manual_ff_usd ?? 0)} · Crypto {fmtUsd(payload.blackvault?.crypto_usd ?? 0)})
                        </div>
                        {payload.audit.totals_balanced ? (
                          <div style={{ color: '#86efac' }}>
                            ✓ {fmtUsd(payload.audit.sum_alchemy_usd)} + {fmtUsd(payload.audit.sum_zenith_usd)} + {fmtUsd(payload.audit.sum_blackvault_usd)}
                          </div>
                        ) : (
                          <div style={{ color: '#fca5a5' }}>Totals did not balance — see warnings</div>
                        )}
                        {payload.audit.zenith_excluded_alchemy > 0 ? (
                          <div>Zenith skipped {payload.audit.zenith_excluded_alchemy} Alchemy row(s) on shared MM account</div>
                        ) : null}
                        {payload.audit.zenith_excluded_blackvault > 0 ? (
                          <div>Zenith skipped {payload.audit.zenith_excluded_blackvault} PulseDigital row(s) (in shop total instead)</div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {(payload.notes?.length ?? 0) > 0 ? (
                    <div className="mt-3 space-y-2">
                      {payload.notes!.map((n) => (
                        <div key={n} className="rv-note">{n}</div>
                      ))}
                    </div>
                  ) : null}

                  {(payload.warnings?.length ?? 0) > 0 ? (
                    <div className="mt-3 space-y-2">
                      {payload.warnings!.map((w) => (
                        <div key={w} className="rv-warn">
                          <svg className="mt-0.5 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                          </svg>
                          <span>{w}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="rv-empty">
                  <svg className="rv-empty-icon" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                  Load totals to see category breakdown
                </div>
              )}

              <div className="rv-panel">
                <div className="rv-panel-label">This month (MTD)</div>
                <div className="rv-panel-range">
                  {compareRanges.thisMonth.startIso.slice(0, 10)} → {compareRanges.thisMonth.endIso.slice(0, 10)}
                </div>
                {compareThis?.ok ? (
                  <>
                    <div className="rv-mtd-highlight">
                      <div className="rv-panel-label">PulseDigital shop</div>
                      <div className="rv-mtd-amount font-display">{fmtUsd(thisBvTotal)}</div>
                      <div className="mt-1 text-[0.62rem] tabular-nums" style={{ color: '#5a6d8a' }}>
                        Combined {fmtUsd(thisTotal)}
                      </div>
                      {monthDeltaPct != null ? (
                        <div className={`mt-2 text-[0.62rem] font-semibold tabular-nums ${monthDelta >= 0 ? 'rv-delta--up' : 'rv-delta--down'}`}>
                          {monthDelta >= 0 ? '▲' : '▼'} {Math.abs(monthDeltaPct).toFixed(1)}% vs full last month
                        </div>
                      ) : null}
                    </div>
                    <div className="rv-panel-stack">
                      <MonthSingleCard brand={BRAND_SLUG.Alchemy} title="Alchemy" theme={siteTheme.alchemy} usd={compareThis.alchemy?.usd ?? 0} prevUsd={compareLast?.alchemy?.usd ?? 0} />
                      <MonthSingleCard brand={BRAND_SLUG.Zenith} title="Zenith" theme={siteTheme.zenith} usd={compareThis.zenith?.usd ?? 0} prevUsd={compareLast?.zenith?.usd ?? 0} />
                      <MonthSingleCard brand={BRAND_SLUG.blackvault} title="PulseDigital" theme={siteTheme.blackvault} usd={compareThis.blackvault?.usd ?? 0} prevUsd={compareLast?.blackvault?.usd ?? 0} />
                    </div>
                  </>
                ) : (
                  <EmptyState text="Load month comparison to populate." />
                )}
              </div>
            </div>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function RollingMonthsSummary(props: {
  label: string
  monthCount: number
  combined: number
  blackvaultShop: number
  alchemy: number
  zenith: number
  blackvault: number
  theme: Record<string, { label: string; accent: string }>
}) {
  return (
    <div className="rv-rolling">
      <div className="rv-command-shimmer" aria-hidden />
      <div className="rv-rolling-inner">
        <div>
          <div className="rv-panel-label" style={{ color: '#a78bfa' }}>{props.monthCount}-month rolling total</div>
          <div className="mt-1 text-[0.65rem]" style={{ color: '#5a6d8a' }}>{props.label}</div>
          <div className="rv-rolling-amount font-display">{fmtUsd(props.combined)}</div>
          <div className="mt-2 text-[0.65rem]" style={{ color: '#5a6d8a' }}>
            PulseDigital shop <strong style={{ color: '#d4e8ff' }}>{fmtUsd(props.blackvaultShop)}</strong>
          </div>
        </div>
        <div className="rv-rolling-breakdown">
          {([
            { key: 'alchemy', usd: props.alchemy, accent: props.theme.alchemy.accent },
            { key: 'zenith', usd: props.zenith, accent: props.theme.zenith.accent },
            { key: 'blackvault', usd: props.blackvault, accent: props.theme.blackvault.accent },
          ] as const).map((s) => (
            <div key={s.key} className="flex items-center justify-between gap-4">
              <span style={{ color: s.accent }}>{props.theme[s.key].label}</span>
              <span className="font-semibold text-[#e8e0ec]">{fmtUsd(s.usd)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function LoadingDots() {
  return (
    <span className="inline-flex items-center gap-1.5" aria-hidden>
      <span className="rv-loading-dot" />
      <span className="rv-loading-dot" />
      <span className="rv-loading-dot" />
    </span>
  )
}

function EmptyState(props: { text: string }) {
  return (
    <div className="flex flex-col items-center py-8 text-center">
      <div className="h-9 w-9 rounded-full border border-white/[0.08]" style={{ background: 'rgba(255,255,255,0.03)' }} />
      <p className="mt-3 m-0 text-[0.65rem]" style={{ color: '#5a6d8a' }}>{props.text}</p>
    </div>
  )
}

function RevenueShareBar(props: {
  alchemy: number
  zenith: number
  blackvault: number
  theme: Record<string, { label: string; accent: string; barColor: string }>
}) {
  const total = props.alchemy + props.zenith + props.blackvault || 1
  const segments = [
    { key: 'alchemy', usd: props.alchemy, pct: (props.alchemy / total) * 100, color: props.theme.alchemy.barColor, accent: props.theme.alchemy.accent, label: 'Alchemy' },
    { key: 'zenith', usd: props.zenith, pct: (props.zenith / total) * 100, color: props.theme.zenith.barColor, accent: props.theme.zenith.accent, label: 'Zenith' },
    { key: 'blackvault', usd: props.blackvault, pct: (props.blackvault / total) * 100, color: props.theme.blackvault.barColor, accent: props.theme.blackvault.accent, label: props.theme.blackvault.label },
  ]
  return (
    <div className="rv-share">
      <div className="rv-section-title" style={{ marginBottom: 14 }}>
        <span className="rv-section-title-dot" />
        Revenue share
      </div>
      <div className="rv-share-track">
        {segments.map((s) => (
          <div
            key={s.key}
            className="rv-share-seg"
            style={{ width: `${Math.max(s.pct, 1)}%`, background: `linear-gradient(90deg, ${s.color}, ${s.color}cc)` }}
          />
        ))}
      </div>
      <div className="rv-share-legend">
        {segments.map((s) => (
          <div key={s.key} className="rv-share-legend-item">
            <span className="rv-share-swatch" style={{ background: s.color }} />
            <span style={{ color: s.accent }}>{s.label}</span>
            <span className="font-semibold tabular-nums" style={{ color: '#5a6d8a' }}>{s.pct.toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function SiteCard(props: {
  title: string
  brand: string
  theme: { accent: string; border: string; glow: string; bg: string; barColor: string }
  usd: number
  rows: number
  skipped: number
  err?: string
  grandTotal: number
  extra?: ReactNode
}) {
  const pct = props.grandTotal > 0 ? (props.usd / props.grandTotal) * 100 : 0
  return (
    <div className="rv-brand-card" data-brand={props.brand}>
      <div className="rv-brand-card-fill" style={{ height: `${Math.max(pct, 8)}%` }} />
      <div className="rv-brand-card-head">
        <div className="rv-brand-card-name">{props.title}</div>
        <div className="rv-brand-card-pct">{pct.toFixed(0)}%</div>
      </div>
      <div className="rv-brand-card-amount font-display">{fmtUsd(props.usd)}</div>
      <div className="rv-brand-card-foot">
        {props.rows} rows{props.skipped > 0 ? ` · ${props.skipped} unparsed` : ''}
      </div>
      {props.extra}
      {props.err ? <div className="mt-2 text-[0.62rem] text-rose-300">{props.err}</div> : null}
    </div>
  )
}

function CategoryMiniCard(props: {
  title: string
  accent: string
  siteUsd?: number
  items: Array<{ label: string; usd: number }>
}) {
  const catSum = props.items.reduce((s, i) => s + i.usd, 0)
  const headerUsd = props.siteUsd ?? catSum
  const unclassified = Math.round((headerUsd - catSum) * 100) / 100
  return (
    <div className="rv-cat-card">
      <div className="flex items-center justify-between">
        <div className="rv-panel-label">{props.title}</div>
        <div className="text-[0.62rem] font-bold tabular-nums" style={{ color: props.accent }}>{fmtUsd(headerUsd)}</div>
      </div>
      {unclassified > 0.01 ? (
        <p className="m-0 mt-1 text-[0.5rem]" style={{ color: '#5a6d8a' }}>
          +{fmtUsd(unclassified)} unclassified
        </p>
      ) : null}
      <div className="rv-cat-row">
        {props.items.map((item) => {
          const pct = catSum > 0 ? (item.usd / catSum) * 100 : 0
          return (
            <div key={item.label} className="rv-cat-row-item">
              <div className="rv-cat-row-head">
                <span style={{ color: '#5a6d8a' }}>{item.label}</span>
                <span className="tabular-nums text-[#e8ecff]">{fmtUsd(item.usd)}</span>
              </div>
              <div className="rv-cat-row-bar">
                <span style={{ width: `${pct}%`, background: props.accent, opacity: 0.45 }} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function MonthPairCard(props: {
  title: string
  brand: string
  theme: { accent: string; border: string; glow: string; bg: string; barColor: string }
  leftLabel: string
  leftUsd: number
  rightLabel: string
  rightUsd: number
  breakdown?: Array<{ label: string; leftUsd: number; rightUsd: number }>
  leftProducts?: Array<{ product: string; qty: number; usd: number }>
  rightProducts?: Array<{ product: string; qty: number; usd: number }>
}) {
  const delta = props.rightUsd - props.leftUsd
  const pct = props.leftUsd > 0 ? (delta / props.leftUsd) * 100 : null
  const hasBreakdown = (props.breakdown?.length ?? 0) > 0
  const hasProducts = (props.leftProducts?.length ?? 0) > 0 || (props.rightProducts?.length ?? 0) > 0
  return (
    <div className="rv-month-card" data-brand={props.brand}>
      <div className="flex items-center justify-between gap-2">
        <div className="rv-brand-card-name">{props.title}</div>
        <div className={`rv-delta-badge ${delta >= 0 ? 'rv-delta-badge--up' : 'rv-delta-badge--down'}`} style={{ padding: '4px 10px', fontSize: '0.55rem' }}>
          {delta >= 0 ? '+' : ''}{fmtUsd(delta)}
        </div>
      </div>
      <div className="rv-month-cols">
        <div>
          <div className="rv-month-col-label">{props.leftLabel}</div>
          <div className="rv-month-col-value font-display">{fmtUsd(props.leftUsd)}</div>
        </div>
        <div>
          <div className="rv-month-col-label">{props.rightLabel}</div>
          <div className="rv-month-col-value font-display">{fmtUsd(props.rightUsd)}</div>
        </div>
      </div>
      {pct != null ? (
        <div className="mt-1 text-[0.55rem] tabular-nums" style={{ color: '#3d3145' }}>
          {pct >= 0 ? '+' : ''}{pct.toFixed(1)}% MoM
        </div>
      ) : null}
      {hasBreakdown || hasProducts ? <div className="mt-2.5 border-t border-white/[0.04] pt-2.5" /> : null}

      {hasBreakdown ? (
        <details className="rv-details group">
          <summary>
            <div className="rv-details-toggle">
              <span>Categories</span>
              <span className="rv-details-chip group-open:hidden">Show</span>
              <span className="rv-details-chip hidden group-open:inline">Hide</span>
            </div>
          </summary>
          <div className="mt-2 grid grid-cols-2 gap-3 text-[0.65rem] leading-snug" style={{ color: '#5a6d8a' }}>
            <div className="space-y-1">
              {props.breakdown!.map((r) => (
                <div key={`l-${props.title}-${r.label}`} className="flex items-baseline justify-between gap-2">
                  <span className="truncate">{r.label}</span>
                  <span className="shrink-0 tabular-nums text-[#e8ecff]">{fmtUsd(r.leftUsd)}</span>
                </div>
              ))}
            </div>
            <div className="space-y-1">
              {props.breakdown!.map((r) => (
                <div key={`r-${props.title}-${r.label}`} className="flex items-baseline justify-between gap-2">
                  <span className="truncate">{r.label}</span>
                  <span className="shrink-0 tabular-nums text-[#e8ecff]">{fmtUsd(r.rightUsd)}</span>
                </div>
              ))}
            </div>
          </div>
        </details>
      ) : null}

      {hasProducts ? (
        <details className={`rv-details group ${hasBreakdown ? 'mt-2.5' : ''}`} open>
          <summary>
            <div className="rv-details-toggle">
              <span>Products</span>
              <span className="rv-details-chip group-open:hidden">Show</span>
              <span className="rv-details-chip hidden group-open:inline">Hide</span>
            </div>
          </summary>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <ProductMiniTable rows={props.leftProducts ?? []} />
            <ProductMiniTable rows={props.rightProducts ?? []} />
          </div>
        </details>
      ) : null}
    </div>
  )
}

function ProductMiniTable(props: { rows: Array<{ product: string; qty: number; usd: number }> }) {
  return (
    <div>
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-2 text-[0.55rem] font-bold uppercase tracking-widest" style={{ color: '#4a3d4f' }}>
        <div>Product</div>
        <div className="text-right">Qty</div>
        <div className="text-right">Total</div>
      </div>
      <div className="mt-1.5 max-h-44 overflow-auto pr-1 text-[0.65rem] leading-snug" style={{ color: '#5a6d8a' }}>
        {props.rows.length ? (
          <div className="space-y-1">
            {props.rows.map((p) => (
              <div key={p.product} className="grid grid-cols-[1fr_auto_auto] items-baseline gap-x-2">
                <div className="min-w-0 truncate">{p.product}</div>
                <div className="text-right tabular-nums text-[#7a92b8]">{p.qty}</div>
                <div className="text-right tabular-nums text-[#e8ecff]">{fmtUsd(p.usd)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-[#3d3145]">\u2014</div>
        )}
      </div>
    </div>
  )
}

function MonthSingleCard(props: {
  title: string
  brand: string
  theme: { accent: string; border: string; glow: string; bg: string; barColor: string }
  usd: number
  prevUsd?: number
}) {
  const delta = props.prevUsd != null ? props.usd - props.prevUsd : null
  const pct = delta != null && props.prevUsd! > 0 ? (delta / props.prevUsd!) * 100 : null
  return (
    <div className="rv-month-card" data-brand={props.brand}>
      <div className="flex items-center justify-between gap-2">
        <div className="rv-brand-card-name">{props.title}</div>
        {delta != null ? (
          <span className={`rv-delta ${delta >= 0 ? 'rv-delta--up' : 'rv-delta--down'}`}>
            {delta >= 0 ? '▲' : '▼'}{pct != null ? ` ${Math.abs(pct).toFixed(0)}%` : ''}
          </span>
        ) : null}
      </div>
      <div className="rv-brand-card-amount font-display">{fmtUsd(props.usd)}</div>
      {delta != null ? (
        <div className="rv-brand-card-foot">vs last {fmtUsd(props.prevUsd ?? 0)}</div>
      ) : null}
    </div>
  )
}
